import React, { useState } from "react";
import Typography from "../Typography";
import Button from "../Button/Button";
import Dropdown from "../Dropdown/Dropdown";
import Input from "../Input/Input";
import FilterToggle from "../FilterToggle/FilterToggle";
import DateRangePicker from "../DatePickerRange/DatePickerRange";

/**
 * Get responsive column width class based on column count.
 */
const getColumnWidth = (columns) => {
  switch (columns) {
    case 2:
      return "w-full lg:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)]";
    case 3:
      return "w-full lg:w-[calc(33.333%-0.5rem)] md:w-[calc(50%-0.5rem)]";
    case 4:
      return "w-full lg:w-[calc(25%-0.5rem)] md:w-[calc(50%-0.5rem)]";
    default:
      return "w-full lg:w-[calc(33.333%-0.5rem)] md:w-[calc(50%-0.5rem)]";
  }
};

/**
 * Common reusable filter component.
 * Renders a collapsible filter panel with configurable fields.
 *
 * @param {object}   props
 * @param {object}   props.listingFilter      – Current filter values
 * @param {function} props.handleFilterChange  – Default onChange handler for fields
 * @param {function} props.handleFilterReset   – Reset handler
 * @param {function} props.applyFilters        – Apply handler
 * @param {Array}    props.fields              – Field configuration array
 * @param {number}   [props.columns=3]         – Number of columns per row (2, 3, or 4)
 * @param {boolean}  [props.defaultExpanded=false] – Start expanded (panel open; ignored when controlled)
 * @param {boolean}  [props.expanded] – Controlled: panel open state (pair with onExpandedChange)
 * @param {function} [props.onExpandedChange] – Controlled: (next: boolean) => void
 * @param {boolean}  [props.expandOnApply=true] – After clicking Filter, keep panel expanded
 * @param {string}   [props.containerClassName] – Wrapper class
 * @param {string}   [props.buttonWidth]       – Custom width class for button container
 * @param {string}   [props.flexClassName]     – Override flex container class
 *
 * Field config shape:
 * {
 *   type: "input" | "dropdown" | "daterange",
 *   name: string,
 *   label: string,
 *   placeholder?: string,
 *   options?: array,          // dropdown only
 *   searchable?: boolean,     // dropdown only
 *   multiselect?: boolean,    // dropdown only
 *   clearable?: boolean,      // dropdown only (default true)
 *   preserveOptionOrder?: boolean, // dropdown only — keep options order (default false)
 *   noWrapper?: boolean,      // dropdown only
 *   inputType?: string,       // input only – defaults to "text"
 *   onChange?: function,       // custom onChange (overrides handleFilterChange)
 *   value?: any,              // custom value (overrides listingFilter lookup)
 *   visible?: boolean,        // show/hide field (default true)
 *   width?: string,           // custom width class (overrides column width)
 *   zIndex?: boolean,         // add relative z-10 for dropdown overflow
 *   id?: string,              // custom id for input
 * }
 */
const CommonFilter = ({
  listingFilter,
  handleFilterChange,
  handleFilterReset,
  applyFilters,
  fields = [],
  columns = 3,
  defaultExpanded = false,
  expanded,
  onExpandedChange,
  expandOnApply = true,
  containerClassName = "bg-white border rounded-lg mb-3",
  buttonWidth,
  flexClassName,
  label,
  /** When true: hides the Filter button (apply on every change via caller debounce); shows only Reset. */
  liveMode = false,
}) => {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  // Controlled only when a callback is provided. Using `expanded !== undefined` is wrong:
  // `expanded={false}` would be "controlled" with no updater if `onExpandedChange` were omitted,
  // so toggle / Filter clicks would no-op.
  const isControlled = typeof onExpandedChange === "function";
  const isExpanded = isControlled ? Boolean(expanded) : internalExpanded;
  const setExpanded = (next) => {
    if (isControlled) {
      onExpandedChange(next);
    } else {
      setInternalExpanded(next);
    }
  };

  const columnClass = getColumnWidth(columns);

  const visibleFields = fields.filter((f) => f.visible !== false);

  const defaultFlexClass = "flex flex-wrap gap-2 items-end";

  return (
    <div className={containerClassName}>
      {/* Filter Header */}
      <FilterToggle
        isExpanded={isExpanded}
        onToggle={() => setExpanded(!isExpanded)}
        label={label}
      />

      {/* Filter Content */}
      <div
        className={`transition-all duration-300 ease-in-out ${
          isExpanded
            ? "max-h-[min(90vh,1600px)] opacity-100 overflow-visible"
            : "max-h-0 opacity-0 overflow-hidden pointer-events-none"
        }`}
      >
        <div className="p-2 relative">
          <div className={flexClassName || defaultFlexClass} style={{ alignItems: "center" }}>
            {visibleFields.map((field) => {
              const fieldWidth = field.width || columnClass;
              const wrapperClass = `${fieldWidth}${field.zIndex ? " relative" : ""}`;

              if (field.type === "input") {
                return (
                  <div key={field.name} className={wrapperClass}>
                    <div>
                      <Typography
                        variant="text2"
                        component="label"
                        htmlFor={field.id || `${field.name}Input`}
                        className="font-medium text-text-primary mb-1"
                      >
                        {field.label}
                      </Typography>
                      <Input
                        type={field.inputType || "text"}
                        name={field.name}
                        value={
                          field.value !== undefined
                            ? field.value
                            : listingFilter?.[field.name] || ""
                        }
                        onChange={field.onChange || handleFilterChange}
                        id={field.id || `${field.name}Input`}
                        placeholder={field.placeholder || ""}
                        noWrapper
                        className="w-full text-sm border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary"
                      />
                    </div>
                  </div>
                );
              }

              if (field.type === "dropdown") {
                const isMulti = field.multiselect === true;
                const rawValue =
                  field.value !== undefined ? field.value : listingFilter?.[field.name];
                const dropdownValue = isMulti
                  ? Array.isArray(rawValue)
                    ? rawValue
                    : rawValue != null && rawValue !== ""
                      ? [rawValue]
                      : []
                  : rawValue !== undefined && rawValue !== null
                    ? rawValue
                    : "";

                return (
                  <div key={field.name} className={wrapperClass}>
                    <Dropdown
                      label={field.label}
                      name={field.name}
                      value={dropdownValue}
                      onChange={field.onChange || handleFilterChange}
                      options={field.options || []}
                      placeholder={field.placeholder || "Select..."}
                      searchable={field.searchable ?? false}
                      multiselect={isMulti}
                      className="text-sm"
                      noWrapper={field.noWrapper ?? false}
                      clearable={field.clearable ?? true}
                      preserveOptionOrder={field.preserveOptionOrder ?? false}
                    />
                  </div>
                );
              }

              if (field.type === "daterange") {
                return (
                  <div key={field.name} className={wrapperClass}>
                    {field.label && (
                      <label className="block text-sm font-medium text-text-primary mb-1">
                        {field.label}
                      </label>
                    )}
                    <DateRangePicker
                      value={
                        field.value !== undefined
                          ? field.value
                          : listingFilter?.[field.name] || null
                      }
                      onChange={field.onChange || handleFilterChange}
                      placeholder={field.placeholder || "Select Date Range"}
                    />
                  </div>
                );
              }

              return null;
            })}

            {/* Filter / Reset Buttons */}
            <div className={`${buttonWidth || columnClass} pt-5`}>
              <div className="flex gap-1">
                {!liveMode && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      applyFilters?.();
                      if (expandOnApply) setExpanded(true);
                    }}
                    className="btn-filter"
                  >
                    Filter
                  </Button>
                )}
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  onClick={handleFilterReset}
                  className="btn-reset"
                >
                  Reset
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommonFilter;
